import type { Component } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import { Code2, Settings2, SlidersHorizontal } from '@lucide/vue';
import type {
  ConnectAppModule,
  ConnectSettingsSectionView,
  ConnectSettingsTopic,
} from '@blpde/connect';
import type { NxLauncherGroup, NxLauncherItem } from '@blpde/nx-ui';
import type { AppEntry, AppId } from '@/integration';
import {
  developmentSettingsTopics,
  generalSettingsTopics,
  systemSettingsTopics,
} from './connectSettings';

/**
 * One entry of the settings hub: a module with the topics of its settings — an
 * app, or one of Connect's own entries („Allgemein", „System", „Development").
 */
export interface SettingsHubModule {
  /** App id, or `general` / `system` / `development`; the first URL segment of its topics. */
  id: string;
  /** i18n key of the caption (the app's name). */
  labelKey: string;
  icon?: Component;
  topics: readonly ConnectSettingsTopic[];
  /**
   * The module's sections of the one settings list (I-2510): the hub lists them
   * next to its topics, each opens the list on itself. Texts already translated.
   */
  listSections?: readonly ConnectSettingsSectionView[];
  /**
   * The app that ships the topics: their captions are keys of its own table and
   * their panels render with it (I-2284). Unset for Connect's own entries.
   */
  app?: AppId;
  /**
   * The app takes part with list sections only — its module has no `settings`
   * topics at all (the agent). Without a list section to show (its rows are
   * previews, I-2510) the hub leaves it out instead of a disabled stand-in.
   */
  listOnly?: boolean;
}

/** Route name of the hub, `/settings` (`?open=<module>` opens that module's group). */
export const SETTINGS_HUB_ROUTE = 'ConnectSettings';
/** Route name of a settings topic, `/settings/:app/:topic`. */
export const SETTINGS_TOPIC_ROUTE = 'ConnectSettingsTopic';
/** Route name of the one settings list, `/settings/list/:section?` (I-2510). */
export const SETTINGS_LIST_ROUTE = 'ConnectSettingsList';

/** Where the settings list opens a section. */
export function settingsListLocation(section?: string): RouteLocationRaw {
  return { name: SETTINGS_LIST_ROUTE, params: section ? { section } : {} };
}

/** Connect's list section that gets a tile under „System"; it opens the list. */
const SYSTEM_LIST_TILE = 'appearance';

/**
 * The modules with their sections of the settings list: an app's own
 * (`section.app`), Connect's own under „System". Sections of an app that has
 * no hub entry are left out (the app is not used by the account). „System"
 * shows only the Darstellung tile, as before the list existed; Connect's other
 * sections (Formulare, Tabellen, Shell, Suche) stay in the list's own nav and
 * the hub search finds them through that tile. An app that takes part with
 * list sections only and has none to show is left out.
 */
export function withListSections(
  modules: readonly SettingsHubModule[],
  sections: readonly ConnectSettingsSectionView[]
): SettingsHubModule[] {
  return modules.flatMap((module) => {
    if (!module.app && module.id === 'system') {
      const own = sections.filter((section) => !section.app);
      const tile = own.find((section) => section.id === SYSTEM_LIST_TILE);
      if (!tile) return module;
      const keywords = own.flatMap((section) =>
        section === tile ? section.keywords : listSectionKeywords(section)
      );
      return { ...module, listSections: [{ ...tile, keywords }] };
    }
    const own = module.app ? sections.filter((section) => section.app === module.app) : [];
    if (own.length) return { ...module, listSections: own };
    return module.listOnly ? [] : module;
  });
}

/**
 * The list section a topic URL of a module leads to when the module has no
 * such topic (any more) — a page the list replaced (`system/appearance`,
 * `mail/display`): the section of that id, else the module's first one.
 * Undefined when the module has no list section.
 */
export function listSectionForTopic(
  moduleId: string,
  topicId: string,
  sections: readonly ConnectSettingsSectionView[]
): string | undefined {
  const own = sections.filter((section) =>
    moduleId === 'system' ? !section.app : section.app === moduleId
  );
  return (own.find((section) => section.id === topicId) ?? own[0])?.id;
}

/** What the hub search finds a list section by: its words and every field's title and words. */
export function listSectionKeywords(section: ConnectSettingsSectionView): string[] {
  return [
    ...section.keywords,
    ...section.groups.map((group) => group.label),
    ...section.fields.flatMap((field) => [field.label, ...field.keywords]),
  ];
}

/** Where a topic opens: its own shell page (SettingsTopicView). */
export function settingsTopicLocation(moduleId: string, topicId: string): RouteLocationRaw {
  return { name: SETTINGS_TOPIC_ROUTE, params: { app: moduleId, topic: topicId } };
}

/** Path of a module's own crumb, `/settings/<module>` — it leads back to the hub with the module open. */
export function settingsModulePath(moduleId: string): string {
  return `/settings/${moduleId}`;
}

/**
 * Connect's „Allgemein" — what holds for every app: the account's overview,
 * the Design and the spell check's dictionary. Always there, listed first.
 */
export const generalSettingsModule: SettingsHubModule = {
  id: 'general',
  labelKey: 'connect.settingsHub.general',
  icon: SlidersHorizontal,
  topics: generalSettingsTopics,
};

/** Connect's platform settings — always there, no module to load. */
export const systemSettingsModule: SettingsHubModule = {
  id: 'system',
  labelKey: 'connect.settingsHub.system',
  icon: Settings2,
  topics: systemSettingsTopics,
};

/** Connect's developer tools — always there, listed last. */
export const developmentSettingsModule: SettingsHubModule = {
  id: 'development',
  labelKey: 'connect.settingsHub.development',
  icon: Code2,
  topics: developmentSettingsTopics,
};

/** Connect's own entries that come before the apps: „Allgemein". */
export const leadingSettingsModules: readonly SettingsHubModule[] = [generalSettingsModule];

/** Connect's own entries, in hub order; they come after the apps. */
export const shellSettingsModules: readonly SettingsHubModule[] = [
  systemSettingsModule,
  developmentSettingsModule,
];

async function loadModule(
  entry: AppEntry,
  install: (id: AppId) => Promise<ConnectAppModule>
): Promise<SettingsHubModule | null> {
  try {
    const module = await install(entry.id);
    return {
      id: entry.id,
      labelKey: entry.labelKey,
      icon: entry.icon,
      topics: module.settings ?? [],
      app: entry.id,
      ...(module.settings === undefined ? { listOnly: true } : {}),
    };
  } catch (err) {
    console.warn('[settings] could not load the settings topics of', entry.id, err);
    return null;
  }
}

/**
 * Loads the settings topics of every app whose entry sets `settings`, in the
 * shell's app order. The apps' modules are loaded through `install` (the shell's
 * app loader), in parallel; an app whose module fails to load is left out, the
 * rest still show.
 */
export async function loadAppSettingsModules(
  entries: readonly AppEntry[],
  install: (id: AppId) => Promise<ConnectAppModule>
): Promise<SettingsHubModule[]> {
  const loaded = await Promise.all(
    entries.filter((entry) => entry.settings).map((entry) => loadModule(entry, install))
  );
  return loaded.filter((module): module is SettingsHubModule => module !== null);
}

/**
 * The module one topic URL names (`/settings/<id>/…`): one of Connect's own, or the app's
 * with its module loaded. `null` when no contributing app has that id or its
 * module fails to load.
 */
export async function loadSettingsModule(
  id: string,
  entries: readonly AppEntry[],
  install: (id: AppId) => Promise<ConnectAppModule>
): Promise<SettingsHubModule | null> {
  const shellModule = [...leadingSettingsModules, ...shellSettingsModules].find(
    (module) => module.id === id
  );
  if (shellModule) return shellModule;
  const entry = entries.find((candidate) => candidate.settings && candidate.id === id);
  return entry ? loadModule(entry, install) : null;
}

/** A module's topics grouped by their `sectionKey`, in the order each key first appears. */
export interface SettingsTopicSection {
  /** The section's i18n key; `null` for topics without one. */
  key: string | null;
  topics: ConnectSettingsTopic[];
  /** The module's sections of the settings list in this hub section (they come first). */
  listSections: ConnectSettingsSectionView[];
}

export function settingsTopicSections(
  topics: readonly ConnectSettingsTopic[],
  listSections: readonly ConnectSettingsSectionView[] = []
): SettingsTopicSection[] {
  const sections: SettingsTopicSection[] = [];
  const sectionOf = (key: string | null): SettingsTopicSection => {
    let section = sections.find((candidate) => candidate.key === key);
    if (!section) {
      section = { key, topics: [], listSections: [] };
      sections.push(section);
    }
    return section;
  };
  for (const list of listSections) sectionOf(list.sectionKey ?? null).listSections.push(list);
  for (const topic of topics) sectionOf(topic.sectionKey ?? null).topics.push(topic);
  return sections;
}

/**
 * Id of the stand-in item of a module without topics. Its label stays empty, so
 * no search matches it; the hub renders its caption in the item's trailing slot.
 */
export const SETTINGS_HUB_EMPTY_ITEM = '__none';

/**
 * The hub's launcher groups: one per module in the order given, one section per
 * group of the module's topics (a topic without a group lands in a section named
 * like the module, which the launcher shows flat), one item per topic. A module
 * without topics keeps its group with a disabled stand-in item — the launcher
 * would drop an empty group, and taking part in the hub is the module's choice.
 *
 * `t` captions the modules (app names are Connect texts), `topicT` a module's
 * sections and topics — with the app's own table for an app (I-2284).
 */
export function buildSettingsHubGroups(
  modules: readonly SettingsHubModule[],
  t: (key: string) => string,
  topicT: (module: SettingsHubModule) => (key: string) => string = () => t
): NxLauncherGroup[] {
  return modules.map((module) => {
    const label = t(module.labelKey);
    const tt = topicT(module);
    const listItem = (section: ConnectSettingsSectionView): NxLauncherItem => ({
      id: `list:${section.id}`,
      // An app's one section reads like the app: in its own group it is „Einstellungen".
      label: section.label === label ? t('connect.settingsHub.listItem') : section.label,
      to: settingsListLocation(section.id),
      keywords: listSectionKeywords(section),
    });
    if (module.topics.length === 0 && !module.listSections?.length) {
      return {
        id: module.id,
        label,
        icon: module.icon,
        sections: [
          {
            id: module.id,
            label,
            items: [{ id: SETTINGS_HUB_EMPTY_ITEM, label: '', disabled: true }],
          },
        ],
      };
    }
    return {
      id: module.id,
      label,
      icon: module.icon,
      sections: settingsTopicSections(module.topics, module.listSections).map((section) => ({
        id: section.key ?? module.id,
        label: section.key ? tt(section.key) : label,
        items: [
          ...section.listSections.map(listItem),
          ...section.topics.map((topic) => ({
            id: topic.id,
            label: tt(topic.labelKey),
            to: settingsTopicLocation(module.id, topic.id),
            keywords: topic.keywords,
          })),
        ],
      })),
    };
  });
}
